import { useLocation } from '@tanstack/react-router'
import { isCommandPaletteShortcut } from 'features/commandPalette/utils/shortcut'
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface CommandPaletteContextValue {
  isEnabled: boolean
  isOpen: boolean
  openCommandPalette: () => void
  closeCommandPalette: () => void
  siteId: string | undefined
  orgId: string | undefined
}

const CommandPaletteContext = createContext<
  CommandPaletteContextValue | undefined
>(undefined)

/** The Entry Browser page binds Cmd+K to its own palette. */
const ENTRY_BROWSER_PATH = '/entry-browser'

interface CommandPaletteProviderProps {
  enabled: boolean
  siteId: string | undefined
  orgId: string | undefined
  children: ReactNode
}

/**
 * Owns the palette's open state and the Cmd/Ctrl+K shortcut. The shortcut is
 * caught on `window` in the capture phase so it wins over page-level handlers.
 */
export const CommandPaletteProvider: FC<CommandPaletteProviderProps> = ({
  enabled,
  siteId,
  orgId,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { pathname } = useLocation()
  const yieldsShortcut = !enabled || pathname.startsWith(ENTRY_BROWSER_PATH)
  const openCommandPalette = useCallback(() => {
    if (enabled) setIsOpen(true)
  }, [enabled])
  const closeCommandPalette = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    if (!enabled) setIsOpen(false)
  }, [enabled])

  useEffect(() => {
    if (yieldsShortcut) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isCommandPaletteShortcut(event)) return
      // Suppress the browser default for the whole hold, but only the first
      // press toggles: a held key fires repeated keydowns.
      event.preventDefault()
      event.stopPropagation()
      if (event.repeat) return
      setIsOpen((open) => !open)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [yieldsShortcut])

  // Escape is claimed on `window` in the capture phase so it beats every
  // document-level Escape handler, the dialog's included. Popups anchored to
  // the input (autofill, password managers) can swallow the keydown outright,
  // so the keyup closes as well; closing twice is harmless.
  useEffect(() => {
    if (!isOpen) return
    const onEscapeDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.repeat) return
      event.preventDefault()
      event.stopPropagation()
      setIsOpen(false)
    }
    const onEscapeUp = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', onEscapeDown, true)
    window.addEventListener('keyup', onEscapeUp, true)
    return () => {
      window.removeEventListener('keydown', onEscapeDown, true)
      window.removeEventListener('keyup', onEscapeUp, true)
    }
  }, [isOpen])

  const value = useMemo(
    () => ({
      isEnabled: enabled,
      isOpen,
      openCommandPalette,
      closeCommandPalette,
      siteId,
      orgId,
    }),
    [closeCommandPalette, enabled, isOpen, openCommandPalette, orgId, siteId]
  )

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  )
}

export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext)
  if (!context) {
    throw new Error(
      'useCommandPalette must be used within CommandPaletteProvider'
    )
  }
  return context
}
